import { Alert, Button, Form, Input, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../../core/service/api-service';

/**
 * Parola sıfırlama — giriş ekranının kanal seçici akışının aynısı:
 *
 *   1) kullanıcı adı            → ForgotPassword  (challenge alınır, KOD GÖNDERİLMEZ)
 *   2) kanal seçimi (SMS/mail)  → SendResetCode   (kod gönderilir, challenge yenilenir)
 *   3) kod + yeni parola        → ResetPassword
 *
 * Kod alanı sayaçtan BAĞIMSIZ görünür (sunucudaki kod ömrü 3 dk); sayaç yalnız "tekrar gönder"i kilitler.
 */
export default function ForgotPasswordForm({ onBack }) {
    const { t } = useTranslation();
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    const [params, setParams] = useState({
        enabled: false,     // challenge alındı → kanal seçimi görünür
        sent: false,        // kod gönderildi   → kod + yeni parola alanları görünür
        key: null,
        timeout: 0,
        sms_auth: false,
        mail_auth: false,
        sms_text: '',
        mail_text: '',
    });

    const startTimer = () => {
        const interval = setInterval(() => {
            setParams((prev) => {
                if (prev.timeout - 1 === 0) clearInterval(interval);
                return { ...prev, timeout: prev.timeout - 1 };
            });
        }, 1000);
    };

    const sendCode = async (type) => {
        setError('');

        const res = await ApiService.Auth.SendResetCode({
            code_key: params.key,
            code_type: type,
        });

        if (res.success) {
            setParams((prev) => ({
                ...prev,
                sent: true,
                key: res.data?.access_token,    // her gönderimde challenge yenilenir
                timeout: 60,                    // yalnız "tekrar gönder" bekleme süresi
            }));
            startTimer();
        }
    };

    const submit = async (form) => {
        setError('');

        // 1. adım: kullanıcı adı
        if (!params.enabled) {
            const res = await ApiService.Auth.ForgotPassword({ username: form.username });

            if (res.success) {
                setParams({
                    enabled: true,
                    sent: false,
                    key: res.data?.access_token,
                    timeout: 0,
                    sms_auth: res.data?.sms_auth,
                    mail_auth: res.data?.mail_auth,
                    sms_text: res.data?.sms_text,
                    mail_text: res.data?.mail_text,
                });
            }
            return;
        }

        // 3. adım: kod + yeni parola
        const res = await ApiService.Auth.ResetPassword({
            code_key: params.key,
            code: form.code,
            newPassword: form.newPassword,
        });

        if (res.success) setDone(true);
    };

    if (done) {
        return (
            <>
                <Alert
                    type="success"
                    showIcon
                    message={t('Your password has been reset.')}
                    description={t('All of your other sessions have been signed out. You can log in with your new password.')}
                />
                <Button style={{ width: '100%', height: 40, marginTop: 16 }} type="primary" onClick={onBack}>
                    {t('Back to login')}
                </Button>
            </>
        );
    }

    return (
        <Form layout={'vertical'} autoComplete="off" onFinish={submit}>
            <Form.Item
                label={t('Username')}
                name="username"
                rules={[{ required: true, message: t('Please input your username!') }]}
            >
                <Input size={'large'} disabled={params.enabled} />
            </Form.Item>

            {params.enabled && (
                <>
                    <Form.Item label={' '} colon={false}>
                        <div style={{ width: '100%', wordWrap: 'break-word', wordBreak: 'break-word' }}>
                            {t('Where should we send the reset code?')}
                        </div>
                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {params.sms_auth && (
                                <Button
                                    size={'large'}
                                    style={{ width: '100%', height: 45, whiteSpace: 'normal', wordBreak: 'break-word' }}
                                    disabled={params.timeout > 0}
                                    onClick={() => sendCode('sms')}
                                >
                                    {t('SMS') + ` (*** *** **${params.sms_text})`}
                                </Button>
                            )}
                            {params.mail_auth && (
                                <Button
                                    size={'large'}
                                    style={{ width: '100%', height: 45, whiteSpace: 'normal', wordBreak: 'break-word' }}
                                    disabled={params.timeout > 0}
                                    onClick={() => sendCode('mail')}
                                >
                                    {t('Mail') + ` (${params.mail_text})`}
                                </Button>
                            )}
                        </div>
                    </Form.Item>

                    {params.sent && (
                        <>
                            <Form.Item
                                label={t('Code')}
                                name="code"
                                rules={[{ required: true, message: t('Please input sms verification code!') }]}
                            >
                                <Input
                                    size={'large'}
                                    maxLength={6}
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    placeholder="______"
                                    addonAfter={<div>{params.timeout > 0 && ` (${params.timeout})`}</div>}
                                />
                            </Form.Item>

                            <Form.Item
                                label={t('New password')}
                                name="newPassword"
                                rules={[
                                    { required: true, message: t('Please input your password!') },
                                    { min: 8, message: t('Password must be at least 8 characters.') },
                                ]}
                            >
                                <Input.Password size={'large'} autoComplete="new-password" />
                            </Form.Item>
                        </>
                    )}
                </>
            )}

            <Typography.Text style={{ color: '#ff0000', fontWeight: 'bold' }}>{t(error)}</Typography.Text>

            {/* Kod gönderilmeden gönder butonu gösterilmez: kullanıcı önce kanal seçmeli. */}
            {(!params.enabled || params.sent) && (
                <Form.Item>
                    <Button style={{ width: '100%', height: 40 }} type="primary" htmlType="submit">
                        {!params.enabled ? t('Continue') : t('Reset password')}
                    </Button>
                </Form.Item>
            )}

            <Button type="link" style={{ padding: 0 }} onClick={onBack}>
                {t('Back to login')}
            </Button>
        </Form>
    );
}
